
import React, { useState, useEffect, useRef } from 'react';
import { Language } from '../../types';
import { TRANSLATIONS } from '../../constants';

interface LogEntry {
  id: string;
  type: 'input' | 'success' | 'error' | 'info';
  content: string;
  timestamp: Date;
}

interface TerminalComponentProps {
  language: Language;
  onExecute: (cmd: string) => Promise<{success: boolean, msg: string}>;
  onBack: () => void;
}

const TerminalComponent: React.FC<TerminalComponentProps> = ({ language, onExecute, onBack }) => {
  const [input, setInput] = useState('');
  const [logs, setLogs] = useState<LogEntry[]>([{
    id: 'init',
    type: 'info',
    content: language === 'ar' ? 'نظام لهجة متصل... جاهز لتلقي الأوامر' : 'Lahja OS connected... Ready for commands',
    timestamp: new Date()
  }]);
  const logsEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  
  const isRtl = language === 'ar';
  const t = TRANSLATIONS[language];

  useEffect(() => {
    logsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [logs]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim()) return;

    const currentInput = input.trim();
    setInput('');
    
    // Add input log
    const inputLog: LogEntry = { id: Date.now().toString(), type: 'input', content: currentInput, timestamp: new Date() };
    setLogs(prev => [...prev, inputLog]);

    if (currentInput.toLowerCase() === 'clear') {
       setLogs([]);
       return;
    }

    if (currentInput.toLowerCase() === 'help') {
       const helpMsg = language === 'ar' 
         ? 'الأوامر المتاحة: flow create [name], flow delete [id], plan upgrade [type], service enable [id], clear, help'
         : 'Available commands: flow create [name], flow delete [id], plan upgrade [type], service enable [id], clear, help';
       setLogs(prev => [...prev, { id: Date.now().toString() + 'h', type: 'info', content: helpMsg, timestamp: new Date() }]);
       return;
    }

    try {
      const result = await onExecute(currentInput);
      const outputLog: LogEntry = {
        id: (Date.now() + 1).toString(),
        type: result.success ? 'success' : 'error',
        content: result.msg,
        timestamp: new Date()
      };
      setLogs(prev => [...prev, outputLog]);
    } catch (err) {
      setLogs(prev => [...prev, { id: Date.now().toString() + 'err', type: 'error', content: 'Execution error.', timestamp: new Date() }]);
    }
  };

  return (
    <div className="flex-1 flex flex-col bg-[#050507] text-[#00ff9d] font-mono h-full overflow-hidden select-none">
       {/* Header */}
       <div className="h-14 border-b border-white/5 bg-black/40 flex items-center justify-between px-6 shrink-0">
          <div className="flex items-center gap-3">
             <button onClick={onBack} className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center text-slate-400 transition-colors">
                <i className={`fas fa-arrow-${isRtl ? 'right' : 'left'}`}></i>
             </button>
             <span className="text-xs font-black tracking-widest text-slate-400 uppercase">LAHJA-TERMINAL v3.0.4</span>
          </div>
          <div className="flex gap-1.5">
             <div className="w-3 h-3 rounded-full bg-rose-500/20"></div>
             <div className="w-3 h-3 rounded-full bg-amber-500/20"></div>
             <div className="w-3 h-3 rounded-full bg-emerald-500/80 animate-pulse shadow-[0_0_10px_#10b981]"></div>
          </div>
       </div>

       {/* Log Area */}
       <div className="flex-1 overflow-y-auto p-6 custom-scrollbar space-y-3" onClick={() => inputRef.current?.focus()}>
          {logs.map(log => (
             <div key={log.id} className="animate-in fade-in slide-in-from-left-2 duration-300">
                <div className="flex gap-3 text-[12px] leading-relaxed">
                   <span className="text-slate-600 shrink-0">[{log.timestamp.toLocaleTimeString([], {hour12: false})}]</span>
                   {log.type === 'input' && <span className="text-blue-400 shrink-0 font-black">❯</span>}
                   {log.type === 'success' && <span className="text-emerald-400 shrink-0 font-black">✓</span>}
                   {log.type === 'error' && <span className="text-rose-500 shrink-0 font-black">✗</span>}
                   {log.type === 'info' && <span className="text-amber-400 shrink-0 font-black">ℹ</span>}
                   <p className={`whitespace-pre-wrap break-all ${log.type === 'error' ? 'text-rose-400' : log.type === 'input' ? 'text-white' : ''}`}>
                      {log.content}
                   </p>
                </div>
             </div>
          ))}
          <div ref={logsEndRef} />
       </div>

       {/* Input Area */}
       <div className="p-4 bg-black/60 border-t border-white/5 shrink-0">
          <form onSubmit={handleSubmit} className="flex items-center gap-3">
             <span className="text-emerald-500 font-black">lahja@root:~$</span>
             <input 
               ref={inputRef}
               autoFocus
               type="text" 
               value={input}
               onChange={(e) => setInput(e.target.value)}
               className="flex-1 bg-transparent border-none focus:ring-0 outline-none text-white text-[13px] caret-emerald-500"
               placeholder={t.terminalPlaceholder}
             />
          </form>
       </div>
    </div>
  );
};

export default TerminalComponent;
